import { Object3D, UVMapping, Vector3 } from "three";
import { CACHEITEMSTATE } from "../../engine/internal";
import { isDefined, isNull } from "../../internal";
import { QuadtreeUtils } from "../../utils/internal";
import { ImageQuadtree, QuadtreeDisplayMode, Tile } from "./internal";
/**
 * Perspective Quadtree Image Class
 * Map Coordinates are:
 *
 *   <img src="../../art/quadtreedefinition.png" alt="" />
 *
 * @author Robert Wüest, Benjamin Loesch
 * @class PerspectiveImageQuadtree
 * @param {SceneGraphNode} parentNode
 * @extends {SceneGraphNode}
 * @constructor
 */
export class PerspectiveImageQuadtree extends ImageQuadtree {
    constructor(baseUrls, tileWidth, imageWidth = 512, imageHeight, minZoom = 1, maxZoom = 3, cachingRange = 15, position, orientation, pixelSize, near, far, stereoright, hideonload) {
        super(baseUrls, tileWidth, imageWidth / (Math.pow(2, maxZoom) * tileWidth), imageHeight / (Math.pow(2, maxZoom) * tileWidth), pixelSize, minZoom, maxZoom, cachingRange);
        /**
         * list all available tiles
         * @property {Object} tiles
         * @default {}
         */
        this.tiles = {};
        this.panX = 0;
        this.panY = 0;
        this.zoom = 1.0;
        this.curZoom = 1;
        this.gamma = 1.0;
        this.position = new Vector3(0, 0, 0);
        this.orientation = new Vector3(0, 0, 0);
        this.near = 0.021;
        this.far = 1200;
        /**
         * Used to synchronize disparityquadtree
         * @property {Function} stoppedEvent
         * @default null
         */
        this.stoppedEvent = null;
        this.onZoom = null;
        this.zoomPrev = -1;
        this.onPan = null;
        this.panXPrev = -1;
        this.panYPrev = -1;
        /**
         * @property {boolean} set to true if this quadtree is used to display the right images in a stereo configuration
         * @type {boolean}
         */
        this.stereoright = false;
        this.requestedImageIndex = -1;
        this.lod0timer = -1;
        this.lod0cbf = null;
        this.cachingStepSize = 1;
        this.frameskippedcbf = null;
        this._lod0ready = false;
        if (isDefined(position)) {
            this.position = new Vector3(position[0], position[1], position[2]);
        }
        if (isDefined(orientation)) {
            this.orientation = new Vector3(orientation[0], orientation[1], orientation[2]);
        }
        this.near = near || 0.021;
        this.far = far || 1200;
        this.stereoright = stereoright || false;
        if (stereoright)
            this._cachingRange = 1;
        if (this.baseUrls.length < this._cachingRange)
            this._cachingRange = this.baseUrls.length;
    }
    init(traversalState) {
        this.quadtreeScene = traversalState.scene.tQuadtreeScene;
        super.init(traversalState);
        this.tileContainer = new Object3D();
        this.quadtreeScene.add(this.tileContainer);
    }
    exit() {
        return;
    }
    /**
     * reads:   traversalState.lockRendering
     *          traversalState.zoom;
     *          traversalState.panX;
     *          traversalState.panY;
     *          traversalState.scene;
     *
     * writes:  traversalState.actualQuadtree
     *
     * @method render
     *
     * @param {TraversalState} traversalState
     */
    render(traversalState) {
        super.render(traversalState);
        if (this.hide || this.currentImageIndex == -1)
            return;
        // --- Measure view quadtree is active
        if (this.active) {
            if (this._imageChanged)
                this.wipeTiles();
            if (!this._allTilesReady)
                traversalState.lockRendering = true;
            // Travers
            this.zoom = traversalState.zoom;
            this.panX = traversalState.panX;
            this.panY = traversalState.panY;
            if (this.zoom != this.zoomPrev) {
                if (!isNull(this.onZoom))
                    this.onZoom(Math.round(this.zoom * 100) / 100);
            }
            this.zoomPrev = Math.round(this.zoom * 100) / 100;
            if (this.panX != this.panXPrev || this.panY != this.panYPrev) {
                if (!isNull(this.onPan)) {
                    this.onPan(Math.round(this.panX), Math.round(this.panY));
                }
            }
            this.panXPrev = Math.round(this.panX);
            this.panYPrev = Math.round(this.panY);
            //Originally the floor value of zoom was used here, but this leads to
            //oversampling (due to the zoom, the screenres was higher than the
            // quadtree res). Was that intentional??
            this.curZoom = Math.min(Math.max(Math.ceil(this.zoom), 1), this.maxZoom);
            // Render
            if (this.baseUrls[this.currentImageIndex] != "" && !this.inputoverflow) {
                // Calculate optimized frustum list:
                this._calcFrustumList();
            }
            this._allTilesReady = true;
            for (const idx in this.tiles) {
                if (this.tiles[idx].ready == false && !this.tiles[idx].nodata) {
                    this._allTilesReady = false;
                    break;
                }
            }
            for (let x = 0; x < this.lstFrustumTiles.length; x++) {
                this.lstFrustumTiles[x].render(traversalState);
            }
            if (!this._allTilesReady && this._allTilesReadyPrev) {
                //first cycle in which some tiles are not ready
                if (this.visibleTilesNotReady)
                    this.visibleTilesNotReady();
            }
            if (this._allTilesReady && !this._allTilesReadyPrev) {
                //first cycle in which all tiles are ready loaded again
                if (this.visibleTilesReady)
                    this.visibleTilesReady();
            }
            this._allTilesReadyPrev = this._allTilesReady;
            //check if image was changed and fire callback
            if (this._imageChanged) {
                this._imageChanged = false;
                if (this.framechangedcbf != null)
                    this.framechangedcbf(this.currentImageIndex);
            }
            this._forceLod0prev = this._forceLod0;
        }
    }
    /**
     * Read the pixel color value in RGBA [0.0 .. 1.0]
     * @method readPixelValue
     * @param {number} x
     * @param {number} y
     * @param {boolean} absolute
     * @return {number[]}
     */
    readPixelValue(x, y, absolute) {
        let map = [0, 0];
        if (isDefined(absolute) && absolute == true) {
            map = [x, y];
        }
        else {
            map = this.mouseToMap(x, y);
        }
        for (let i = 0; i < this.lstFrustumTiles.length; i++) {
            const tile = this.lstFrustumTiles[i];
            if (map[0] >= tile.extent[0] &&
                map[0] < tile.extent[2] &&
                map[1] >= tile.extent[1] &&
                map[1] < tile.extent[3]) {
                // point is inside this tile!
                const retVal = tile.getPixelValue(map[0], map[1]);
                return [retVal[0], retVal[1], retVal[2], retVal[3]];
            }
        }
        return [0.0, 0.0, 0.0, 0.0]; // nothing found, return no data value
    }
    /**
     * Wipe all active tiles and set the current quadtree image set
     * @method setImageIndex
     * @param {number} index
     *
     */
    setImageIndex(index, playmode = false, cachingstepsize) {
        if (index == this.requestedImageIndex && playmode)
            return;
        // determine displayMode
        this.determineDisplayMode(index, playmode);
        // check if image is available
        this.requestedImageIndex = index;
        this.cachingStepSize = cachingstepsize || 1;
        // special case if it's the first time a framenumber is called.
        // Send cache not ready to disable waitingWheel...
        if (this.displayMode == QuadtreeDisplayMode.PLAYINGFORWARD ||
            this.displayMode == QuadtreeDisplayMode.PLAYINGBACKWARD ||
            this.currentImageIndex == -1) {
            this._cacheSequence();
            // is cache ready? then change image directly
            if (this._cachingCompleted) {
                this.changeToRequestedImage();
            }
            else {
                if (!isNull(this.cacheNotReadyEvent) && !this._cacheNotReadyEventSent) {
                    this.cacheNotReadyEvent();
                    this._cacheNotReadyEventSent = true;
                }
            }
        }
        else {
            switch (this.displayMode) {
                case QuadtreeDisplayMode.FRAMESTEPPINGFORWARD:
                case QuadtreeDisplayMode.FRAMESTEPPINGBACKWARD:
                    this.forceLod0(true);
                    this.changeToRequestedImage();
                    this._cacheSequence();
                    break;
                case QuadtreeDisplayMode.DIRECT:
                case QuadtreeDisplayMode.FRAMESLIDINGFORWARD:
                case QuadtreeDisplayMode.FRAMESLIDINGBACKWARD:
                    this.changeToRequestedImage();
                    break;
                default:
                    break;
            }
        }
        //check if image was not changed within 200ms so load tiles
        this.lod0cbf = () => {
            if (!playmode &&
                !(this.displayMode == QuadtreeDisplayMode.FRAMESLIDINGFORWARD) &&
                !(this.displayMode == QuadtreeDisplayMode.FRAMESLIDINGBACKWARD)) {
                this.forceLod0(false);
            }
            if (!playmode && this.stoppedEvent != null)
                this.stoppedEvent();
            this.currentTraversalState.scene.render();
        };
        window.clearTimeout(this.lod0timer);
        this.lod0timer = window.setTimeout(() => {
            this.lod0cbf();
        }, 200);
    }
    /**
     * Determines the display mode according call-frequency and requested framenumbers...
     * @param index
     * @param playmode
     */
    determineDisplayMode(index, playmode) {
        if (this.stereoright) {
            this.displayMode = QuadtreeDisplayMode.DIRECT;
        }
        else {
            super.determineDisplayMode(index, playmode);
        }
    }
    /**
     * Activates measure view
     * @method setActive
     * @param {boolean} active
     */
    setActive(active) {
        this.active = active;
        if (active) {
            this.wipeTiles();
            this.currentTraversalState.scene.render();
        }
    }
    requestImageTile(url, quadCode, cbf) {
        const res = QuadtreeUtils.QuadKeyToTileCoord(quadCode);
        let image_url = url + "/0/?lod=" + res["lod"] + "&x=" + res["x"] + "&y=" + res["y"];
        if (this.stereoright) {
            image_url = url + "/1/?lod=" + res["lod"] + "&x=" + res["x"] + "&y=" + res["y"];
        }
        const successCbf = (image) => {
            cbf({ quadCode: quadCode, loaded: true, image: image });
        };
        const errorCbf = () => {
            cbf({ quadCode: quadCode, loaded: false, image: null });
        };
        this.currentTraversalState.scene.engine.requestImage(image_url, successCbf, errorCbf, false);
    }
    mouseToMap(mx, my) {
        return [
            (mx - this.panX) / this.zoom / (2 * this.tileWidth),
            (my - this.panY) / this.zoom / (2 * this.tileWidth),
        ];
    }
    /**
     * convert map position (pixel) to mouse position
     * @method mapToMouse
     * @param {number} px
     * @param {number} py
     * @return {*[]}
     */
    mapToMouse(px, py) {
        return [px * 2 * this.tileWidth * this.zoom + this.panX, py * 2 * this.tileWidth * this.zoom + this.panY];
    }
    _subDivide(x0, y0, x1, y1) {
        const i = this._iterator.count;
        if (this.lstFrustumTiles[i].quadCode.length == this.curZoom) {
            this._iterator.count++;
            return; // no further subdivision: only zoom to current zoom level!
        }
        const coords = QuadtreeUtils.QuadKeyToNormalizedCoord(this.lstFrustumTiles[i].quadCode);
        // check if this.lstFrustum[i]  is inside (x0,y0)-(x1,y1)
        // if so, request children
        let tileReadyCount = 0;
        const tileReady = (ready, texture) => {
            if (ready)
                tileReadyCount += 1;
        };
        if (!(coords[0] > x1 || coords[2] < x0 || coords[1] > y1 || coords[3] < y0)) {
            const quadCodeTop = this.lstFrustumTiles[i].quadCode;
            for (let j = 0; j < 4; j++) {
                if (!isDefined(this.tiles[quadCodeTop + j.toString()])) {
                    this.tiles[quadCodeTop + j.toString()] = new Tile(quadCodeTop + j.toString(), this.baseUrls[this.currentImageIndex], this.tileWidth, this.imageWidth, this.imageHeight, this.currentTraversalState, tileReady, this.stereoright);
                }
            }
            if (this.tiles[quadCodeTop + "0"].isAvailable() &&
                this.tiles[quadCodeTop + "1"].isAvailable() &&
                this.tiles[quadCodeTop + "2"].isAvailable() &&
                this.tiles[quadCodeTop + "3"].isAvailable()) {
                this.tileContainer.remove(this.lstFrustumTiles[i].tMesh);
                this.lstFrustumTiles.splice(i, 1);
                for (let k = 0; k < 4; k++) {
                    if (this.tiles[quadCodeTop + k.toString()].ready) {
                        this.lstFrustumTiles.push(this.tiles[quadCodeTop + k.toString()]);
                        this.tileContainer.add(this.tiles[quadCodeTop + k.toString()].tMesh);
                    }
                }
            }
            else {
                this._iterator.count++;
            }
            return;
        }
        this._iterator.count++;
    }
    /**
     * Calc quadtree (private function, do not call)
     * @method _divide
     * @param {number} x0
     * @param {number} y0
     * @param {number} x1
     * @param {number} y1
     * @private
     */
    _divide(x0, y0, x1, y1) {
        this._iterator.count = 0;
        while (this._iterator.count != this.lstFrustumTiles.length) {
            this._subDivide(x0, y0, x1, y1);
        }
    }
    /**
     * Optimize frustum list (private function, do not call)
     * @method _Optimize
     * @param {number} x0
     * @param {number} y0
     * @param {number} x1
     * @param {number} y1
     * @private
     */
    _optimize(x0, y0, x1, y1) {
        let i = this.lstFrustumTiles.length - 1;
        while (i > -1) {
            const tile = this.lstFrustumTiles[i];
            if (tile.nodata) {
                this.tileContainer.remove(this.lstFrustumTiles[i].tMesh);
                this.lstFrustumTiles.splice(i, 1);
            }
            else {
                if (!(tile.extent[0] > x1 || tile.extent[2] < x0 || tile.extent[1] > y1 || tile.extent[3] < y0)) {
                    // tile is visible!
                }
                else {
                    // tile is not visible, remove from frustum list
                    this.tileContainer.remove(this.lstFrustumTiles[i].tMesh);
                    this.lstFrustumTiles.splice(i, 1);
                }
            }
            i--;
        }
    }
    /**
     * Calculate quadtree (private function, do not call)
     * @method _calcFrustumList
     * @private
     * @return {boolean} True if Frustum is ready to be rendered, else returns false.
     */
    _calcFrustumList() {
        // Callback Function
        let tileReady;
        // Create 1 Tile LOD 0
        if (!isDefined(this.tiles[""]) || this.tiles[""] == null) {
            this.tiles[""] = new Tile("", this.baseUrls[this.currentImageIndex], this.tileWidth, this.imageWidth, this.imageHeight, this.currentTraversalState, tileReady, this.stereoright);
        }
        // Create 4 Tiles LOD 1
        if (!this._forceLod0) {
            for (let j = 0; j < 4; j++) {
                // create tiles if not visible
                if (!isDefined(this.tiles[j.toString()]) || this.tiles[j.toString()] == null) {
                    this.tiles[j.toString()] = new Tile(j.toString(), this.baseUrls[this.currentImageIndex], this.tileWidth, this.imageWidth, this.imageHeight, this.currentTraversalState, tileReady, this.stereoright);
                }
            }
        }
        const ctx = this.currentTraversalState.scene.engine.renderer.domElement;
        const r00 = this.mouseToMap(0, 0);
        const r11 = this.mouseToMap(ctx.width - 1, ctx.height - 1);
        // cleanup frustum first
        for (let k = 0; k < this.lstFrustumTiles.length; k++) {
            this.tileContainer.remove(this.lstFrustumTiles[k].tMesh);
        }
        this.lstFrustumTiles = [];
        if (this._forceLod0) {
            // add the first tile LOD 0
            if (this.tiles[""].ready) {
                this.lstFrustumTiles.push(this.tiles[""]);
                this.tileContainer.add(this.tiles[""].tMesh);
            }
        }
        else {
            // add the first 4 tiles LOD 1
            let lod1Unavailable = false;
            for (let k = 0; k < 4; k++) {
                if (!this.tiles[k.toString()].isAvailable()) {
                    lod1Unavailable = true;
                    break;
                }
                if (this.tiles[k.toString()].ready) {
                    this.lstFrustumTiles.push(this.tiles[k.toString()]);
                    this.tileContainer.add(this.tiles[k.toString()].tMesh);
                }
            }
            if (lod1Unavailable) {
                if (this.tiles[""].ready) {
                    this.lstFrustumTiles.push(this.tiles[""]);
                    this.tileContainer.add(this.tiles[""].tMesh);
                }
            }
            else {
                this._divide(r00[0], r00[1], r11[0], r11[1]); // Subdivide
                this._optimize(r00[0], r00[1], r11[0], r11[1]); // Optimize frustum list
            }
        }
        /**
         * QUADTREE DEBUG FUNCTION
         */
        const showQuadTree = function (frustumList) {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            canvas.width = 128;
            canvas.height = 128;
            for (let rx = 0; rx < frustumList.length; rx++) {
                const tileCoords = QuadtreeUtils.QuadKeyToTileCoord(frustumList[rx].quadCode);
                const tileSize = Math.floor(128.0 / Math.pow(2, frustumList[rx].quadCode.length));
                ctx.beginPath();
                ctx.rect(tileCoords.x * tileSize, tileCoords.y * tileSize, tileSize, tileSize);
                ctx.lineWidth = 2;
                ctx.strokeStyle =
                    frustumList[rx].quadCode.length > 2
                        ? "#DDAA00"
                        : frustumList[rx].quadCode.length > 1
                            ? "#DD6600"
                            : "#DD0000";
                ctx.stroke();
            }
            // create a new img object
            const image = new Image();
            // set the img.src to the canvas data url
            image.src = canvas.toDataURL();
        };
        return true;
    }
    /**
     * Wipes all tiles from catalog, frustum and scene. this is used before loading a new tiles image
     * @method wipeTiles
     */
    wipeTiles() {
        for (let i = 0; i < this.lstFrustumTiles.length; i++) {
            // clear renderable objects
            this.tileContainer.remove(this.lstFrustumTiles[i].tMesh);
        }
        for (const t in this.tiles) {
            this.tiles[t].destroy();
        }
        // clear frustum
        this.lstFrustumTiles = [];
        this.tiles = {};
    }
    /**
     * @method _cacheSequence
     * @private
     */
    _cacheSequence() {
        if (this.displayMode == QuadtreeDisplayMode.PLAYINGFORWARD ||
            this.displayMode == QuadtreeDisplayMode.FRAMESTEPPINGFORWARD) {
            this._cacheImages(this.requestedImageIndex - 3, this._cachingRange, this._cachingDepth, null, this.cachingStepSize);
        }
        if (this.displayMode == QuadtreeDisplayMode.PLAYINGBACKWARD ||
            this.displayMode == QuadtreeDisplayMode.FRAMESTEPPINGBACKWARD) {
            this._cacheImages(this.requestedImageIndex + 3, this._cachingRange, this._cachingDepth, null, this.cachingStepSize);
        }
    }
    _cacheImages(index, range, depth, frustumCulled, cachingStepSize) {
        const reverse = this.displayMode == QuadtreeDisplayMode.PLAYINGFORWARD ||
            this.displayMode == QuadtreeDisplayMode.FRAMESTEPPINGFORWARD
            ? false
            : true;
        //make sure index or range does not overlap streamlength
        if (index < 0)
            index = 0;
        const framelist = ImageQuadtree.clampRangeToFramelist(index, range, cachingStepSize, reverse, 0, this.baseUrls.length - 1);
        this._cachingCompleted = false;
        const urlsToLoad = framelist.map((x) => {
            const sensorid = this.stereoright ? 1 : 0;
            return ImageQuadtree.getOverviewImageUrl(this.baseUrls[x], sensorid);
        });
        if (this.currentImageIndex < this.baseUrls.length) {
            this.waitforurl = this.baseUrls[this.requestedImageIndex] + "/" + [this.faceindex] + "/?lod=0&x=0&y=0";
        }
        else {
            this.waitforurl = "";
        }
        this._cachingCompleted = false;
        this._cachingQueueSize = urlsToLoad.length;
        if (urlsToLoad.length == 0)
            this._cachingCompleted = true;
        for (let it = urlsToLoad.length - 1; it >= 0; it--) {
            //check if url is not already in loading state
            const urltoload = urlsToLoad[it];
            const state = this.currentTraversalState.scene.engine.isInTextureCache(urltoload);
            if (state == CACHEITEMSTATE.LOADING || state == CACHEITEMSTATE.LOADED) {
                if (state == CACHEITEMSTATE.LOADED) {
                    this._cachingQueueSize -= 1;
                    if (urltoload.indexOf(this.waitforurl) > -1) {
                        this._cachingCompleted = true;
                    }
                }
                urlsToLoad.splice(it, 1);
            }
        }
        for (let it = 0; it < urlsToLoad.length; it++) {
            this.currentTraversalState.scene.engine.requestTexture(urlsToLoad[it], UVMapping, (texture) => {
                this._successFunction(texture);
            }, () => {
                this._failFunction();
            }, false);
        }
    }
    changeToRequestedImage() {
        this.currentTraversalState.scene.render();
        if (this.cacheReadyEvent != null)
            this.cacheReadyEvent();
        this._cacheNotReadyEventSent = false;
        if (this.currentImageIndex == this.requestedImageIndex && this._forceLod0)
            return;
        this.currentImageIndex = this.requestedImageIndex;
        this._imageChanged = true;
        this.currentTraversalState.scene.render();
    }
    _successFunction(texture) {
        // SUCCESS CODES
        this._cachingQueueSize -= 1;
        if (texture.name.indexOf(this.waitforurl) > -1 || this.waitforurl == "") {
            this._cachingCompleted = true;
            this.changeToRequestedImage();
        }
    }
    _failFunction() {
        console.log("FAILED");
        this._cachingQueueSize -= 1;
    }
    /**
     * This function waits for all tiles to be loaded. Returns false
     * if an error occurs or else it returns true.
     * @method waitForAllTiles
     * @return {boolean}
     */
    waitForAllTiles() {
        console.log("PerspectiveImageQuadtree.waitForAllTiles: This Method seems to be deprecated...?");
        let quadtreeLevelReached = false;
        for (let i = 0; i < this.lstFrustumTiles.length; i++) {
            const tile = this.lstFrustumTiles[i];
            if (tile.quadCode.length != this.curZoom)
                continue; //quadtree is not yet in the current lod.
            quadtreeLevelReached = true;
            if (this.lstFrustumTiles[i].image.failed)
                return false;
            if (!this.lstFrustumTiles[i].isAvailable())
                return false;
        }
        return quadtreeLevelReached;
    }
}
//# sourceMappingURL=perspectiveimagequadtree.js.map